import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { IProduct } from '@monofront/types';
import ProductPrice from '@monofront/design-system/components/ProductPrice';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import QuestionMark from '@monofront/design-system/visuals/Icons/QuestionMark';
import ProductAddToCart from '../ProductAddToCart';
import {
  getCurrentProductQuantity,
  getHasHiddenPrice,
  getIsAntiWaste,
  getMaxQuantity,
  getMinimumProductQuantity,
} from '@monofront/business/utils';
import { useAuthentication } from '@context/businessContainer';
import { useMediaQuery } from '@monofront/business/hooks';
import ButtonAddToWishlist from '@components/ButtonAddToWishlist/ButtonAddToWishlistDEPRECATED';
import Popover from '@components/Popover';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import ProductNotifyMe from '../ProductNotifyMe';
import { useEditorialSidePanelContext } from '@monofront/business/contexts';
import { ABTEST_STROKE_PRICE } from '@monofront/business/constants';
import ABTestEnabled from '@components/ABTestEnabled';
import useI18n from '@hooks/i18n/useI18n';

interface IProductPriceActionsProps {
  product: IProduct;
  id?: string;
  withWishlistButton?: boolean;
  withMinimalistRefPrice?: boolean;
  UNSAFE_className?: string;
}

const ProductPriceActions: React.FC<IProductPriceActionsProps> = ({
  product,
  id,
  withWishlistButton = false,
  withMinimalistRefPrice = false,
  UNSAFE_className = '',
}) => {
  const tCatalog = useTranslations('Catalog');
  const tCart = useTranslations('Cart');
  const { isGermanMarket } = useI18n();
  const isMobile = useMediaQuery(800);
  const { isAuthenticated } = useAuthentication();
  const { shoppingCart } = useShoppingCart();
  const shouldHidePrice = Boolean(
    !isAuthenticated && getHasHiddenPrice(product),
  );
  const maxQuantity = getMaxQuantity(product);
  const currentProductQuantity = getCurrentProductQuantity(
    product,
    shoppingCart.products,
  );
  const isAntiWaste = getIsAntiWaste(product.tags);
  const minimumQuantity = getMinimumProductQuantity(product.tags);
  const { setEditorialPanelSourceUid, setEditorialSidePanelProps } =
    useEditorialSidePanelContext();

  const isMaxInventoryReached =
    product.inventory_quantity > 0 && maxQuantity <= currentProductQuantity;

  return (
    <section
      id={id}
      className={`
        tw-fixed tw-inset-x-0 tw-bottom-0
        tw-z-30 tw-mt-5
        tw-flex tw-items-center tw-gap-2 tw-border-0 tw-border-y
        tw-border-solid
        tw-border-blue-200 tw-bg-white tw-px-3
        tw-py-3 desktop:tw-relative
        desktop:tw-px-0
        ${isMaxInventoryReached ? 'tw-pb-5' : ''} ${UNSAFE_className}
      `}
    >
      {shouldHidePrice ? (
        <Link
          href="/account/register/flow"
          className="tw-lf-link-md tw-grow tw-self-center tw-pr-2"
          rel="nofollow"
        >
          {tCatalog('seeMemberPrice')}
        </Link>
      ) : (
        <div className="tw-grow">
          <ProductPrice
            product={product}
            quantity={currentProductQuantity}
          >
            <ABTestEnabled.Provider feature={ABTEST_STROKE_PRICE}>
              <ABTestEnabled.A>
                <span className="tw-ml-2 tw-flex tw-items-center">
                  {!withMinimalistRefPrice && tCatalog('nonMemberPrice')}
                  <Popover content={<p>{tCatalog('referencePriceDetails')}</p>}>
                    <span className="tooltip-icon tw-ml-1 [&_svg]:tw-align-top">
                      <QuestionMark size={IconSizes.sm} aria-hidden />
                    </span>
                  </Popover>
                </span>
              </ABTestEnabled.A>
              <ABTestEnabled.B>
                <button
                  type="button"
                  className="tw-lf-button-ghost tw-ml-2 tw-flex tw-cursor-pointer tw-items-center tw-text-xs tw-text-black-1000 tw-transition tw-duration-300 tw-ease-in-out hover:tw-text-black-1000 hover:tw-opacity-60"
                  onClick={() => {
                    setEditorialPanelSourceUid(
                      isAntiWaste ? 'stroke-price-anti-waste' : 'stroke-price',
                    );
                    setEditorialSidePanelProps({ product });
                  }}
                >
                  {!withMinimalistRefPrice && tCatalog('nonMemberPrice')}
                  <span className="tooltip-icon tw-ml-1 [&_svg]:tw-align-top">
                    <QuestionMark size={IconSizes.sm} aria-hidden />
                  </span>
                </button>
              </ABTestEnabled.B>
            </ABTestEnabled.Provider>
          </ProductPrice>
          {isGermanMarket && (
            <p className="tw-mt-1 tw-text-xs tw-text-black-700">
              {tCatalog.rich('taxShippingNotice', {
                shippingLink: (chunks) => (
                  <Link
                    href="/pages/die-lieferung"
                    className="tw-underline"
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </p>
          )}
        </div>
      )}

      <div className="tw-flex tw-gap-2">
        {product.inventory_quantity <= 0 && !isAntiWaste ? (
          <ProductNotifyMe product={product} />
        ) : (
          <div className="tw-relative">
            <ProductAddToCart product={product} />
            {isMaxInventoryReached && minimumQuantity === 1 && (
              <p className="tw-lf-error-message tw-absolute tw-bottom-[-1.4em] tw-w-full tw-text-center tw-text-xs">
                {tCatalog('endOfStock')}
              </p>
            )}
            {currentProductQuantity > 0 && minimumQuantity > 1 && (
              <p className="tw-lf-caption-regular tw-pt-[2px] tw-text-center tw-text-black-700">
                {tCart('minimumProductAmount', {
                  minimumQuantity,
                })}
              </p>
            )}
          </div>
        )}

        {withWishlistButton && !isMobile && (
          <ButtonAddToWishlist
            variantId={product.objectID}
            name={product.title}
            sku={product.sku}
            price={product.price}
            comparedPrice={product.compare_at_price}
            large
            hasButtonLook
            image={product.image}
            product={product}
          />
        )}
      </div>
      <style jsx>{`
        @media (max-width: 800px) {
          :global(#main-footer) {
            padding-bottom: 5em;
          }
        }
      `}</style>
    </section>
  );
};

export default ProductPriceActions;
